import NetEqualisationInterestPaymentTable from './NetEqualisationInterestPaymentTable';
import EqualisationInterestCalculationsItem from './EqualisationInterestCalculationsItem';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import EqualisationFooter from '../EqualisationFooter';
import { EqualisationSection } from 'further-types/lp/equalisation';
import { useEqualisationSection } from '../EqualisationContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { EqualisationInterestFormValues } from './FormWrapper';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { round, sumBy } from 'lodash';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import EqualisationPage from '../EqualisationPage';
import TypographyButton from 'further-ui/components/TypographyButton';
import { ExpandLess } from '@mui/icons-material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';

interface EqualisationInterestContentProps {
  baseCurrency: CurrencyCode;
  calculationBreakdown: Array<{
    id: string;
    name: string;
    ledgerDate: CalendarDay;
    totalAmountLcy: number;
    newInvestorBreakdown: Array<{
      investorName: string;
      commitmentId: string;
      investments: number;
      fees: number;
      expenses: number;
      workingCapital: number;
      returnOfCapital: number;
      netCallOrDistribution: number;
      callOverpaidOrUnderpaid: number;
      runningTotalCallOverpaidOrUnderpaid: number;
      equalisationInterestTotalLcy: number;
      equalisationInterestPercentage: number;
    }>;
  }>;
}

const EqualisationInterestContent: React.FC<
  EqualisationInterestContentProps
> = ({ baseCurrency, calculationBreakdown }) => {
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();
  const { getValues, watch } = useFormContext<EqualisationInterestFormValues>();
  const equalisationInterestPaymentBreakdown = watch(
    'equalisationInterestPaymentBreakdown',
  );

  const {
    previousSection,
    nextSection,
    currentSection,
    goToNextSection,
    goToPreviousSection,
    goToListPage,
  } = useEqualisationSection();

  const watchedBreakdown = watch('equalisationInterestPaymentBreakdown');
  const totalEqualisationInterestLcy =
    round(sumBy(watchedBreakdown, 'equalisationInterestTotalLcy') ?? 0, 2) === 0
      ? 0
      : round(sumBy(watchedBreakdown, 'equalisationInterestTotalLcy') ?? 0, 2);

  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getInterest,
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationActionAllocationsQueryKey],
    }),
  );

  const [showCalculations, setShowCalculations] = useState(false);

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        equalisationInterestPaymentBreakdown: getValues(
          'equalisationInterestPaymentBreakdown',
        ),
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handleSaveAsDraft = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection,
        equalisationInterestPaymentBreakdown: getValues(
          'equalisationInterestPaymentBreakdown',
        ),
      },
      {
        onSuccess: () => {
          goToListPage();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
        equalisationInterestPaymentBreakdown: getValues(
          'equalisationInterestPaymentBreakdown',
        ),
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  const columns: Array<TableColumn<(typeof calculationBreakdown)[number]>> = [
    {
      key: 'name',
      renderColorIndicator: ({ name }) => ({
        colorKey: { name },
        content: name,
      }),
    },
    {
      key: 'ledgerDate',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'totalAmountLcy',
      render: (row) =>
        numberToCurrencyString(row.totalAmountLcy, { currency: baseCurrency }),
    },
  ];

  return (
    <EqualisationPage
      subheading="Process equalisation interest."
      actionBar={
        <EqualisationFooter
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          handleSaveAsDraft={handleSaveAsDraft}
          disabled={totalEqualisationInterestLcy !== 0}
          loading={updateEqualisation.isPending}
          tooltipDisabledMessage="Please ensure that the total equalisation interest payment breakdown equals 0."
        />
      }
    >
      <NetEqualisationInterestPaymentTable
        netEqualisationInterestPaymentBreakdown={
          equalisationInterestPaymentBreakdown
        }
        baseCurrency={baseCurrency}
        totalEqualisationInterestLcy={totalEqualisationInterestLcy}
      />

      <TypographyButton
        disableRipple
        onClick={() => setShowCalculations(!showCalculations)}
        textDecoration="none"
        endAdornment={
          showCalculations ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )
        }
        text={
          showCalculations
            ? 'Hide full calculation breakdown'
            : 'Show full calculation breakdown'
        }
      />

      {showCalculations && (
        <Table
          hideHeader
          onRowExpand={({ id }) => toggleRowExpansion(id)}
          onRowClick={({ id }) => toggleRowExpansion(id)}
          isExpandedRow={({ id }) => expandedRows.has(id)}
          isExpandableRow={() => true}
          getExpandedContent={(row, _, index) => (
            <EqualisationInterestCalculationsItem
              key={index}
              action={row}
              baseCurrency={baseCurrency}
              index={index}
              isLastElement={index === calculationBreakdown.length - 1}
            />
          )}
          columns={columns}
          rows={calculationBreakdown}
        />
      )}
    </EqualisationPage>
  );
};

export default EqualisationInterestContent;
